/* eslint-disable react-hooks/exhaustive-deps */
import { yupResolver } from "@hookform/resolvers/yup";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { Box, Modal, Typography } from "@mui/material";
import TextFeild from "../../components/CommonComponents/TextFeild";
import { INPUT_LABELS } from "../../constants/lang";
import StyledEditBrandDetails from "./StyledEditBrandDetails";
import ImageCropper from "../../components/CommonComponents/ImageCroper";
import { ToastNotifyError, ToastNotifySuccess } from "../../components/CommonComponents/Toast/ToastNotify";
import AddIcon from "@mui/icons-material/Add";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { EditProfileSchema } from "../../schema/CreateProfileSchem";
import { uploadImage } from "../../store/reducers/authentication/apiThunk";
import { useDispatch } from "react-redux";
import { fetchEditSellerProfile } from "../../store/reducers/createProfile";
import rollbar from "../../rollbarConfig";

const ACCEPT_OPTIONS = "image/png, image/jpeg, image/jpg, image/webp";

const EditBrandDetails = forwardRef(function EditBrandDetails(props, ref) {
  const { getData } = props;
  const inputFileRef = useRef(null);
  const dispatch = useDispatch();
  const [croppedImage, setCroppedImage] = useState(null);
  const [openCropperModal, setOpenCropperModal] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [croppedImageFile, setCroppedImageFile] = useState(null);
  const [profileImage, setProfileImage] = useState(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(EditProfileSchema),
  });

  const onSubmit = async (data) => {
    if (profileImage === null) {
      ToastNotifyError("Profile Picture is Mandatory");
      window.scrollTo(0, 0);
    } else {
      const dataToSend = {
        brand_image: profileImage,
        brand_bio: data?.brandWriteUp,
        product_description: data?.productsProcesses,
        fav_about_nyc: data?.favAboutNYC,
        designer_bio: data?.bio,
      };

      try {
        const res = await dispatch(fetchEditSellerProfile(dataToSend));
        const { status, message } = res?.payload || {};
        if (status) {
          getData();
          ToastNotifySuccess(message);
        } else {
          ToastNotifyError(message);
        }
      } catch (err) {
        rollbar.error("Error message", err);
        console.error(err);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit(onSubmit)();
    },
  }));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file) return;
      if (!ACCEPT_OPTIONS.includes(file.type)) {
        ToastNotifyError(
          "Invalid file type! Please select a PNG, JPEG, JPG, or WEBP image."
        );
        e.target.value = "";
        return;
      }
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        ToastNotifyError("File size must be less than 5MB");
        e.target.value = "";
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageUrl(reader.result);
        setOpenCropperModal(true);
        e.target.value = "";
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const uploadDataImage = async () => {
      try {
        const response = await dispatch(uploadImage(croppedImageFile));
        const { key, uploadResponse } = response?.payload;
        const { status } = uploadResponse || {};
        if (status === 200) {
          const finalImage = process.env.REACT_APP_S3_IMAGES_URL + key;
          setProfileImage(finalImage);
        }
      } catch (err) {
        rollbar.error("Error message", err);
        console.error(err);
      }
    };
    if (croppedImageFile !== null) {
      uploadDataImage();
    }
  }, [croppedImageFile]);

  const handleCancelCropper = () => {
    setOpenCropperModal(false);
    setImageUrl(null);
  };

  const handleImageCrop = ({ base64, file }) => {
    setCroppedImage(base64);
    setCroppedImageFile(file);
    setImageUrl(null);
    setOpenCropperModal(false);
  };

  useEffect(() => {
    setCroppedImage(props.userData?.brand_image);
    setProfileImage(props.userData?.brand_image);
    setValue("brandWriteUp", props.userData?.brand_bio);
    setValue("productsProcesses", props.userData?.product_description);
    setValue("bio", props.userData?.designer_bio);
    setValue("favAboutNYC", props.userData?.fav_about_nyc);
  }, [props.userData]);

  return (
    <StyledEditBrandDetails>
      <Box className='topic-header'>Brand details</Box>
      <Box>
        <Box className='image-wrap'>
          <Box
            className={`${croppedImage ? "profile-picture-image" : "profile-picture"}`}
            onClick={() => {
              if (!croppedImage) {
                inputFileRef.current.click();
              }
            }}
          >
            {croppedImage ? (
              <Box className='cropped-image-wrap'>
                <img
                  src={croppedImage}
                  alt='cropped-img'
                  className='cropped-image'
                />
                <Box
                  className='image-close'
                  onClick={() => {
                    setCroppedImage(null);
                    setImageUrl(null);
                    setProfileImage(null)
                  }}
                >
                  <CloseRoundedIcon />
                </Box>
              </Box>
            ) : (
              <AddIcon className='add-icon' />
            )}
          </Box>
          <Typography className='add-image-text'>Change image</Typography>
        </Box>
        <input
          type='file'
          ref={inputFileRef}
          accept={"image/png, image/jpeg"}
          style={{ display: "none" }}
          onChange={handleImageChange}
        />{" "}
      </Box>
      <Box
        className='create-profile-topics'
        component='form'
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box className='topic-header-2'>Brand Description</Box>
        <Box className='create-textfeilds'>
          <Controller
            name='brandWriteUp'
            control={control}
            defaultValue=''
            render={({ field }) => (
              <TextFeild
                {...field}
                id='brandWriteUp'
                required
                label={INPUT_LABELS.brandWriteUp}
                placeholder="Provide a concise description of the brand's mission and values"
                variant='outlined'
                fullWidth
                margin='normal'
                multiline={true}
                minRows={4}
                maxRows={4}
                error={!!errors.brandWriteUp}
                helperText={errors.brandWriteUp?.message}
              />
            )}
          />
          <Controller
            name='productsProcesses'
            control={control}
            defaultValue=''
            render={({ field }) => (
              <TextFeild
                {...field}
                is='productsProcesses'
                label={INPUT_LABELS.productsProcesses}
                placeholder="Describe the brand's offerings or processes"
                variant='outlined'
                fullWidth
                margin='normal'
                multiline={true}
                minRows={4}
                maxRows={4}
                error={!!errors.productsProcesses}
                helperText={errors.productsProcesses?.message}
              />
            )}
          />
        </Box>
      </Box>
      <Box className='create-profile-topics'>
        <Box className='topic-header-2'>Designer Bio</Box>
        <Box className='create-textfeilds'>
          <Controller
            name='bio'
            control={control}
            defaultValue=''
            render={({ field }) => (
              <TextFeild
                {...field}
                id='bio'
                required
                label={INPUT_LABELS.bio}
                placeholder='Add information about designers'
                variant='outlined'
                fullWidth
                margin='normal'
                multiline={true}
                minRows={4}
                maxRows={4}
                error={!!errors.bio}
                helperText={errors.bio?.message}
              />
            )}
          />
          <Controller
            name='favAboutNYC'
            control={control}
            defaultValue=''
            render={({ field }) => (
              <TextFeild
                {...field}
                id='favAboutNYC'
                label={INPUT_LABELS.favAboutNYC}
                placeholder='Share what you love most about New York City'
                variant='outlined'
                fullWidth
                margin='normal'
                multiline={true}
                minRows={4}
                maxRows={4}
                error={!!errors.favAboutNYC}
                helperText={errors.favAboutNYC?.message}
              />
            )}
          />
        </Box>
      </Box>
      <Modal
        open={openCropperModal}
        onClose={() => setOpenCropperModal(false)}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(3px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "40%",
            height: "60%",
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 3,
            padding: 2,
          }}
        >
          <Box
            className='image-crop-display'
            sx={{ height: "90%", width: "100%" }}
          >
            {imageUrl && (
              <ImageCropper
                src={imageUrl}
                onCancel={handleCancelCropper}
                onCropComplete={handleImageCrop}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </StyledEditBrandDetails>
  );
});

export default EditBrandDetails;
